import { ComputedRef, computed } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { Announcement, AppMode } from "@/CodegenTypes";
import {
  fetchAnnouncements,
  fetchScrollingText,
  fetchTelegramBotName,
  fetchTnc,
} from "@/api/settingsReads";

export const settingsQueryKeys = {
  all: ["settings"] as const,
  tnc: () => [...settingsQueryKeys.all, "tnc"] as const,
  scrollingText: (appMode: AppMode) =>
    [...settingsQueryKeys.all, "scrolling-text", appMode] as const,
  telegramBotName: (appMode: AppMode) =>
    [...settingsQueryKeys.all, "telegram-bot-name", appMode] as const,
  announcements: (appMode: AppMode) =>
    [...settingsQueryKeys.all, "announcements", appMode] as const,
};

export function useTncQuery() {
  return useQuery<string, unknown>(settingsQueryKeys.tnc(), fetchTnc);
}

export function useScrollingTextQuery(appMode: ComputedRef<AppMode>) {
  return useQuery<string, unknown>(
    computed(() => settingsQueryKeys.scrollingText(appMode.value)),
    () => fetchScrollingText(appMode.value)
  );
}

export function useTelegramBotNameQuery(appMode: ComputedRef<AppMode>) {
  return useQuery<string, unknown>(
    computed(() => settingsQueryKeys.telegramBotName(appMode.value)),
    () => fetchTelegramBotName(appMode.value)
  );
}

export function useAnnouncementsQuery(appMode: ComputedRef<AppMode>) {
  return useQuery<Announcement[], unknown>(
    computed(() => settingsQueryKeys.announcements(appMode.value)),
    () => fetchAnnouncements(appMode.value)
  );
}

export function useSettingsQueryInvalidation() {
  const queryClient = useQueryClient();

  return {
    invalidateTnc: () => queryClient.invalidateQueries(settingsQueryKeys.tnc()),
    invalidateScrollingText: (appMode: AppMode) =>
      queryClient.invalidateQueries(settingsQueryKeys.scrollingText(appMode)),
    invalidateTelegramBotName: (appMode: AppMode) =>
      queryClient.invalidateQueries(settingsQueryKeys.telegramBotName(appMode)),
    invalidateAnnouncements: (appMode: AppMode) =>
      queryClient.invalidateQueries(settingsQueryKeys.announcements(appMode)),
  };
}
